<template>
  <!-- DO NOT CHANGE TO NUXT-LINK. The logout happens at Drupal, so we need a normal link to have a full page reload. -->
  <button
    v-if="banner"
    class="box bg-gradient-to-r relative logo-background group"
    :class="banner.boxColor"
    @click.prevent="() => logoutIfNeededAndRedirect()"
  >
    <div class="relative z-20">
      <div class="w-full box-padding">
        <h2
          class="text-white font-semibold text-lg md:text-xl lg:text-2xl mb-10"
        >
          {{ banner.title }}
        </h2>
        <p class="text-white md:text-md lg:text-lg mb-20">{{ banner.text }}</p>
      </div>

      <div
        class="box-pull-down box-padding py-0 box-pull-down absolute bottom-0 left-0"
      >
        <ButtonStyle
          v-if="drupalUser.isLoggedIn"
          v-slot="{ classes }"
          outlined
          :berry="banner.buttonColor === 'berry'"
          :orange="banner.buttonColor === 'orange'"
        >
          <div :class="classes" class="hover:bg-white">
            {{ ctaText }}
          </div>
        </ButtonStyle>
        <template v-else>
          <ButtonStyle
            v-slot="{ classes }"
            :pink="banner.buttonColor === 'berry'"
            :orange="banner.buttonColor === 'orange'"
          >
            <div :class="classes">
              <SpriteSymbol name="lock" class="w-20 h-20 mr-10" />
              <span>{{ $texts('shop.registerNow', 'Jetzt anmelden') }}</span>
            </div>
          </ButtonStyle>
        </template>
      </div>
    </div>
  </button>
</template>

<script lang="ts" setup>
import type { PaywallBanner } from '@pharmasuisse/types/commerce'
import { useDrupalUser, DrupalRole } from '~/stores/drupalUser'
import { useGlobalConfig } from '~/stores/globalConfig'

const route = useRoute()
const router = useRouter()
const drupalUser = useDrupalUser()
const language = useCurrentLanguage()
const { $texts } = useNuxtApp()
const globalConfig = useGlobalConfig()

const props = defineProps<{
  showPaywall?: Boolean
  requiredRoles: string[]
}>()

// Handle banners.
type Banner = {
  title: string
  text: string
  buttonColor: string
  boxColor: string
}

type BannerFunction = () => Banner

const banner1: BannerFunction = () => ({
  title: $texts(
    'shop.bannerTitle1',
    'Dieser Artikel ist für registrierte Personen bestellbar.',
  ),
  text: $texts(
    'shop.bannerText1',
    'Bitte melden Sie sich mit Ihrem persönlichen Konto an oder erstellen Sie Ihr persönliches Konto, um diesen Artikel zu bestellen.',
  ),
  buttonColor: 'orange',
  boxColor: 'from-orange-light to-orange',
})

const banner2: BannerFunction = () => ({
  title: $texts(
    'shop.bannerTitle2',
    'Dieser Artikel ist für Einzelmitglieder bestellbar.',
  ),
  text: $texts(
    'shop.bannerText2',
    'Bitte melden Sie sich mit Ihrem Mitglieder-Konto an, um diesen Artikel zu bestellen.',
  ),
  buttonColor: 'orange',
  boxColor: 'from-orange-light to-orange',
})

const banner3: BannerFunction = () => ({
  title: $texts(
    'shop.bannerTitle3',
    'Dieser Artikel ist für Einzelmitglieder bestellbar.',
  ),
  text: $texts(
    'shop.bannerText3',
    'Werden Sie noch heute Mitglied des Schweizerischen Apothekerverband pharmaSuisse, um von den Vorteilen zu profitieren.',
  ),
  buttonColor: 'orange',
  boxColor: 'from-orange-light to-orange',
})

const banner4: BannerFunction = () => ({
  title: $texts(
    'shop.bannerTitle4',
    'Dieser Artikel ist für Apotheken bestellbar.',
  ),
  text: $texts(
    'shop.bannerText4',
    'Bitte melden Sie sich mit dem Konto der Apotheken an, um diesen Artikel zu bestellen.',
  ),
  buttonColor: 'berry',
  boxColor: 'from-berry to-pink',
})

const banner5: BannerFunction = () => ({
  title: $texts(
    'shop.bannerTitle5',
    'Dieser Artikel ist für angeschlossene Apotheken bestellbar.',
  ),
  text: $texts(
    'shop.bannerText5',
    'Bitte melden Sie sich mit dem Konto der angeschlossenen Apotheken an, um diesen Artikel zu bestellen.',
  ),
  buttonColor: 'berry',
  boxColor: 'from-berry to-pink',
})

const banner6: BannerFunction = () => ({
  title: $texts(
    'shop.bannerTitle6',
    'Dieser Artikel ist für angeschlossene Apotheken bestellbar.',
  ),
  text: $texts(
    'shop.bannerText6',
    'Schliessen Sie noch heute die Apotheke dem Verband an und profitieren Sie von den Vorteilen.',
  ),
  buttonColor: 'berry',
  boxColor: 'from-berry to-pink',
})

const priorityRoles: Record<string, Record<string, BannerFunction>> = {
  [DrupalRole.NonMember]: {},
  [DrupalRole.NonAffiliatedPharmacy]: {},
}

const targetRole = computed<string>(() => {
  const priorityRole = props.requiredRoles.find((role) => !!priorityRoles[role])
  return priorityRole || props.requiredRoles[0] || ''
})

const ANONYMOUS = 'anonymous'

const USER_PRODUCT_ROLE_MAP: Record<string, Record<string, BannerFunction>> = {
  [ANONYMOUS]: {
    [DrupalRole.NonMember]: banner1,
    [DrupalRole.IndividualMember]: banner2,
    [DrupalRole.AffiliatedPharmacy]: banner5,
    [DrupalRole.NonAffiliatedPharmacy]: banner4,
  },
  [DrupalRole.NonMember]: {
    [DrupalRole.IndividualMember]: banner3,
    [DrupalRole.AffiliatedPharmacy]: banner5,
    [DrupalRole.NonAffiliatedPharmacy]: banner4,
  },
  [DrupalRole.IndividualMember]: {
    [DrupalRole.AffiliatedPharmacy]: banner5,
    [DrupalRole.NonAffiliatedPharmacy]: banner4,
  },
  [DrupalRole.NonAffiliatedPharmacy]: {
    [DrupalRole.NonMember]: banner1,
    [DrupalRole.IndividualMember]: banner2,
    [DrupalRole.AffiliatedPharmacy]: banner6,
  },
  [DrupalRole.AffiliatedPharmacy]: {
    [DrupalRole.NonMember]: banner1,
    [DrupalRole.IndividualMember]: banner2,
  },
}

const banner = computed<PaywallBanner | undefined>(() => {
  if (!targetRole) {
    return
  }
  const matchingRole =
    drupalUser.roles.find((role) => !!USER_PRODUCT_ROLE_MAP[role]) || ANONYMOUS

  const fn = USER_PRODUCT_ROLE_MAP[matchingRole][targetRole.value]
  if (fn) {
    return fn()
  }
})

/*
  Check if product is available only for Einzelmitglied and
  the logged user is Nichtmitglied, to display a
  different banner link for this case scenario.
 */
const isForIndividualMembers = computed(() => {
  return (
    drupalUser?.roles?.includes('non_member') &&
    props.requiredRoles?.includes('individual_member')
  )
})

/*
  Check if product is available only for affiliated pharmacies and
  the logged user is a non-affiliated pharmacy, to display a
  different banner link for this case scenario.
 */
const isForAffiliatedPharmacies = computed(() => {
  return (
    drupalUser?.roles?.includes('non_affiliated_pharmacy') &&
    props.requiredRoles?.includes('affiliated_pharmacy')
  )
})

const linkMemberManagement = computed(
  () => globalConfig.linkForNonMembers || '/',
)

const linkForNonAffiliatedPharmacies = computed(
  () => globalConfig.linkForNonAffiliatedPharmacies || '/',
)

// Displays a different button text based on the case scenarios above.
const ctaText = computed(() => {
  if (isForIndividualMembers.value) {
    return $texts('shop.applyForMembership', 'Jetzt Mitgliedschaft beantragen')
  }

  if (isForAffiliatedPharmacies.value) {
    return $texts(
      'shop.connectPharmacyInfo',
      'Weitere Informationen zum Anschluss der Apotheke',
    )
  }

  return $texts('loginWithAnotherAccount', 'Mit anderem Konto anmelden')
})

async function logoutIfNeededAndRedirect() {
  let destination = router.resolve({
    path: `/${language.value}/user/login`,
    query: {
      destination: route.path,
      role: targetRole.value,
    },
  }).href
  let shouldLogout = false
  if (drupalUser.isLoggedIn) {
    shouldLogout = true
    if (isForIndividualMembers.value) {
      /*
          Links to the logout page, with a nested destination parameter and,
          in special case (described above)Links to custom page set in
          global config under "Link für Nichtmitglied".
        */
      destination = linkMemberManagement.value
      shouldLogout = false
    } else if (isForAffiliatedPharmacies.value) {
      /*
         Link is shown on the Paywall banner if a non-affiliated pharmacy
         tries to access a product available only for affiliated pharmacies.
         Links to custom page set in global config under
         "Link für nicht angeschlossene Apotheken".
       */
      destination = linkForNonAffiliatedPharmacies.value
      shouldLogout = false
    }
  }
  if (shouldLogout) {
    await drupalUser.logout()
  }
  if (process.client) {
    // The function will only get called on the client side, which is ok as
    // we are reacting to a click event.
    window.location.href = destination
  }
}
</script>

<style lang="postcss"></style>
